import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';
import { cx } from '../utils/cx.js';
import styles from './Input.module.css';

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label?: string;
  hint?: string;
  error?: string;
  showErrorText?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  {
    label,
    hint,
    error,
    className,
    id,
    showErrorText = true,
    required,
    'aria-describedby': ariaDescribedBy,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? rest.name ?? autoId;
  const errorId = `${inputId}-error`;
  const describedBy = [ariaDescribedBy, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={cx(styles.wrap, className)}>
      {label ? (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={inputId}
        className={cx(styles.input, error && styles.error)}
        style={{ minHeight: 96, resize: 'vertical' }}
        aria-invalid={!!error}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        required={required}
        {...rest}
      />
      {showErrorText && error ? (
        <span id={errorId} className={styles.errText} role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className={styles.hint}>{hint}</span>
      ) : null}
    </div>
  );
});
